/**
 * Верхний отступ под статус-бар телефона. В браузере и в Telegram его даёт
 * `env(safe-area-inset-top)`, а Android WebView внутри нашего Flutter-приложения
 * часто отвечает там нулём — и шапка ложится под часы и значки сети.
 *
 * Поэтому приложение-обёртка может сказать высоту само: открыть страницу с
 * `?safe_top=<логические px>` (во Flutter — `MediaQuery.of(context).padding.top`).
 * Значение запоминается на сессию, чтобы перезагрузка и переходы его не теряли.
 * CSS берёт большее из двух: `--safe-top` в app.css.
 */
import { whenBridgeReady } from './nativeBridge'

const KEY = 'dorixona.safeTop'

/**
 * Оболочка открыта от края до края, а высоту не передала: берём высоту
 * статус-бара с запасом, как progress-app-new, — лучше лишний воздух, чем часы
 * поверх логотипа.
 */
export const NATIVE_SAFE_TOP = 40

/** Высота из адреса: целое число от 0 до 200, иначе нет значения. */
export function parseSafeTop(search: string): number | null {
  const raw = new URLSearchParams(search).get('safe_top')
  if (raw === null || !/^\d{1,3}$/.test(raw)) return null
  const value = Number(raw)
  return value <= 200 ? value : null
}

function remembered(): number | null {
  try {
    return parseSafeTop(`?safe_top=${window.sessionStorage.getItem(KEY) ?? ''}`)
  } catch {
    return null
  }
}

function remember(value: number) {
  try {
    window.sessionStorage.setItem(KEY, String(value))
  } catch {
    // Хранилище закрыто (приватный режим) — отступ живёт до перезагрузки.
  }
}

/** Взять высоту из адреса или из сессии и отдать её CSS. */
export function applySafeTop(): void {
  const fromUrl = parseSafeTop(window.location.search)
  if (fromUrl !== null) remember(fromUrl)
  const value = fromUrl ?? remembered()
  if (value !== null) {
    document.documentElement.style.setProperty('--app-safe-top', `${value}px`)
    return
  }
  void whenBridgeReady().then((bridge) => {
    if (bridge) document.documentElement.style.setProperty('--app-safe-top', `${NATIVE_SAFE_TOP}px`)
  })
}
